import { useLocation } from '@reach/router';
import Link from '../LocalizedLink';
import React, { FC, useEffect, useId, useRef, useState } from 'react';

import arrowDown from '../../static/images/arrow_down.svg';
import useOutsideClick from '../../hooks/useOutsideClick';
import { DEFAULT_LANGUAGE, LANGUAGE_CODES, SUPPORTED_LANGUAGES } from '../../../languages';

import { Arrow, Wrapper, Menu, Label, LinkWrapper } from './styles';

const LanguageSelector: FC = () => {
  const location = useLocation();

  const [isOpened, setIsOpened] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuId = useId();
  useOutsideClick(ref, () => setIsOpened(false));

  const pathSegments = location.pathname.split('/');
  const routeLanguage = LANGUAGE_CODES.find((code) => code === pathSegments[1]);
  const currentLanguage = routeLanguage || DEFAULT_LANGUAGE;
  const label = currentLanguage.toUpperCase();
  const links = SUPPORTED_LANGUAGES.slice(1).filter((l) => !l.alias);
  const unprefixedPath = routeLanguage ? `/${pathSegments.slice(2).join('/')}` : location.pathname;
  const toPage = `${unprefixedPath || '/'}${location.search || ''}${location.hash || ''}`;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' && isOpened) {
      event.preventDefault();
      setIsOpened(false);
      buttonRef.current?.focus();
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    if (!isOpened) {
      setIsOpened(true);
      return;
    }
    const items = Array.from(ref.current?.querySelectorAll<HTMLAnchorElement>('[role="menuitem"]') || []);
    const currentIndex = items.indexOf(document.activeElement as HTMLAnchorElement);
    const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 :
      currentIndex < 0 ? (event.key === 'ArrowUp' ? items.length - 1 : 0) :
      (currentIndex + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
    items[nextIndex]?.focus();
  };

  useEffect(() => {
    if (!isOpened) return;

    const selectedLangElement = ref.current?.querySelector<HTMLAnchorElement>('[data-selected="true"] a');
    selectedLangElement?.focus({ preventScroll: true });
    selectedLangElement?.scrollIntoView({ block: 'nearest' });
  }, [isOpened]);

  return (
    <Wrapper
      ref={ref}
      onKeyDown={handleKeyDown}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setIsOpened(false);
      }}
      onScroll={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <Label
        ref={buttonRef}
        type="button"
        aria-label={`Language: ${label}`}
        aria-haspopup="menu"
        aria-expanded={isOpened}
        aria-controls={isOpened ? menuId : undefined}
        className={isOpened ? 'isOpened' : ''}
        onClick={() => setIsOpened((value) => !value)}
      >
        {label}
        <Arrow className={isOpened ? 'isOpened' : ''} src={arrowDown} alt="" aria-hidden="true" />
      </Label>
      {isOpened && (
        <Menu id={menuId} role="menu" aria-label="Languages">
          {links.map(({ code, label: languageName }) => (
            <LinkWrapper
              key={code}
              role="none"
              className={currentLanguage === code ? 'selected' : ''}
              data-selected={(currentLanguage === code).toString()}
            >
              <Link
                to={toPage}
                language={code}
                role="menuitem"
                aria-label={languageName || code}
                aria-current={currentLanguage === code ? 'page' : undefined}
                tabIndex={currentLanguage === code ? 0 : -1}
                onClick={() => setIsOpened(false)}
              >
                {code.toLocaleUpperCase()}
              </Link>
            </LinkWrapper>
          ))}
        </Menu>
      )}
    </Wrapper>
  );
};

export default LanguageSelector;
